import React, { createContext, useState, useContext, ReactNode } from "react";
import { getUserScore } from "@/services/UserService";
import { useGuestContext } from "@/context/GuestContext";
import { UUID } from "crypto";
import { logger } from "@/services/LoggerService";

interface Score {
    id: UUID;
    score: number;
    createdAt: string;
    // Add other score properties as needed
}

interface ScoreContextType {
    hasFetchedDataState: boolean;
    totalScore: number;
    scores: Score[];
    updateTotalScore: (addedScore: number, isAuthenticated?: boolean) => void;
    fetchUserDailyScore: (isAuthenticated: boolean, userId: string, areaId: UUID) => Promise<void>;
}

const ScoreContext = createContext<ScoreContextType | undefined>(undefined);

interface ScoreProviderProps {
    children: ReactNode;
}

export const ScoreProvider = ({ children }: ScoreProviderProps) => {
    const [totalScore, setTotalScore] = useState<number>(0);
    const [scores, setScores] = useState<Score[]>([]);
    const { totalGuestPlayScoreDaily } = useGuestContext();
    const [hasFetchedDataState, setHasFetchedDataState] = useState<boolean>(false);

    let hasFetchedData = false;

    /**
     * Updates the total score based on authentication status.
     * For authenticated users, adds the given score to the current totalScore state.
     * For guest users, recalculates the total score from localStorage (addedScore parameter is ignored).
     *
     * @param addedScore - The score to be added to the total score.
     * @param isAuthenticated - Whether the user is authenticated.
     */
    const updateTotalScore = (addedScore: number, isAuthenticated: boolean = true): void => {
        if (isAuthenticated) {
            setTotalScore((prevTotalScore) => prevTotalScore + addedScore);
        } else {
            // For guest users, recalculate from localStorage to ensure accuracy
            setTotalScore(totalGuestPlayScoreDaily());
        }
    };

    /**
     * Fetches the daily score for a user, considering authentication status and area information.
     *
     * @param isAuthenticated - Indicates whether the user is authenticated.
     * @param userId - The ID of the user for whom to fetch the daily score.
     * @param areaId - The ID of the area for which to fetch the daily score.
     *
     * @returns Resolves once the user's daily score is fetched and processed.
     *
     * Note: The function ensures that data is fetched only once to avoid redundant API calls.
     *
     * @throws Throws an error if there's an issue fetching the user score.
     */
    const fetchUserDailyScore = async (isAuthenticated: boolean, userId: string, areaId: UUID): Promise<void> => {
        if (!hasFetchedData) {
            try {
                hasFetchedData = true;

                if (isAuthenticated) {
                    const response = await getUserScore(userId, areaId, true);
                    const dailyScore = response as unknown as { dailyScore: { scores: Score[]; totalScore: number } };

                    setScores(dailyScore.dailyScore.scores);
                    setTotalScore(dailyScore.dailyScore.totalScore);
                } else {
                    setTotalScore(totalGuestPlayScoreDaily());
                }

                setHasFetchedDataState(true);
            } catch (error) {
                hasFetchedData = false;
                setHasFetchedDataState(false);

                // Handle error if the API call fails
                console.error("Error fetching user score:", error);
                logger.error("Error fetching user score", error, {
                    userId,
                    areaId,
                    isAuthenticated,
                    context: "ScoreContext",
                });
            }
        }
    };

    return (
        <ScoreContext.Provider value={{ hasFetchedDataState, totalScore, scores, updateTotalScore, fetchUserDailyScore }}>
            {children}
        </ScoreContext.Provider>
    );
};

export const useScore = (): ScoreContextType => {
    const context = useContext(ScoreContext);
    if (context === undefined) {
        throw new Error("useScore must be used within a ScoreProvider");
    }
    return context;
};
